"use client";

import { useEffect, useState } from "react";
import {
  AlertCircle,
  RefreshCcw,
  Home,
  Mail,
  Code2,
  Terminal,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Link from "next/link";
import GlassCard from "@/components/creations/glass-card";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleReset = async () => {
    setIsResetting(true);
    await new Promise((resolve) => setTimeout(resolve, 500)); // Visual feedback
    reset();
    setIsResetting(false);
  };

  const errorMessage =
    error.message || "An unexpected error occurred. Please try again.";
  const isDevelopment = process.env.NODE_ENV === "development";

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-linear-to-br from-background via-background to-destructive/5">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-destructive/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse delay-700" />
      </div>

      <div className="relative z-10 w-full max-w-3xl">
        {/* Main Error Card */}
        <GlassCard
          variant="error"
          blur="xl"
          glow="strong"
          shine="medium"
          animated
          className="p-8 md:p-12 mb-6"
        >
          {/* Error Icon Header */}
          <div className="flex flex-col items-center text-center mb-8">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl animate-pulse" />
              <div className="relative bg-red-500/10 p-6 rounded-full border-2 border-red-500/40">
                <AlertCircle className="h-16 w-16 text-red-500" />
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-outline-error-glow text-4xl md:text-5xl font-black mb-4">
              Oops! Something Broke!
            </h1>

            <div className="h-1 w-24 bg-linear-to-r from-transparent via-red-500 to-transparent mb-6" />

            {/* Error Description */}
            <p className="text-foreground/80 text-lg max-w-md">
              Don't worry, even the best code has bugs. Let's get you back on
              track.
            </p>
          </div>

          {/* Error Message Card */}
          <GlassCard variant="destructive" blur="md" className="p-6 mb-6">
            <div className="flex items-start gap-3">
              <Code2 className="h-5 w-5 text-destructive shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-outline-destructive-fg font-semibold mb-2">
                  Error Details
                </h3>
                <p className="text-sm text-foreground/70 wrap-break-word font-mono bg-background/50 p-3 rounded border border-destructive/20">
                  {errorMessage}
                </p>
                {error.digest && (
                  <p className="text-xs text-foreground/50 mt-2 font-mono">
                    Error ID: {error.digest}
                  </p>
                )}
              </div>
            </div>
          </GlassCard>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <button
              onClick={handleReset}
              disabled={isResetting}
              className="group flex items-center justify-center gap-2 px-6 py-3 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-all hover:scale-105 shadow-lg hover:shadow-destructive/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCcw
                className={`h-5 w-5 ${isResetting ? "animate-spin" : "group-hover:rotate-180"} transition-transform`}
              />
              <span className="font-semibold">
                {isResetting ? "Retrying..." : "Try Again"}
              </span>
            </button>

            <Link href="/">
              <button className="group flex items-center justify-center gap-2 px-6 py-3 border-2 border-primary/50 text-foreground rounded-lg hover:bg-primary/10 transition-all hover:scale-105 w-full">
                <Home className="h-5 w-5 group-hover:scale-110 transition-transform" />
                <span className="font-semibold">Go Home</span>
              </button>
            </Link>
          </div>

          {/* Stack Trace Toggle (Development Only) */}
          {isDevelopment && error.stack && (
            <div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="w-full flex items-center justify-between px-4 py-3 bg-background/50 border border-border/50 rounded-lg hover:bg-background/70 transition-all mb-2"
              >
                <div className="flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-foreground/60" />
                  <span className="text-sm font-medium text-foreground/80">
                    Developer Stack Trace
                  </span>
                </div>
                {showDetails ? (
                  <ChevronUp className="h-4 w-4 text-foreground/60" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-foreground/60" />
                )}
              </button>

              {showDetails && (
                <GlassCard variant="subtle" blur="sm" className="p-4">
                  <pre className="text-xs text-foreground/70 overflow-x-auto font-mono whitespace-pre-wrap wrap-break-word">
                    {error.stack}
                  </pre>
                </GlassCard>
              )}
            </div>
          )}
        </GlassCard>

        {/* Help Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Contact Support */}
          <GlassCard
            variant="frosted"
            blur="md"
            glow="subtle"
            hover
            className="p-6"
          >
            <Mail className="h-8 w-8 text-primary mb-3" />
            <h3 className="text-outline-fg-primary font-semibold mb-2">
              Need Help?
            </h3>
            <p className="text-sm text-foreground/70 mb-4">
              If this error persists, feel free to reach out.
            </p>
            <Link href="/contact">
              <button className="text-sm text-primary hover:text-primary/80 font-medium hover:underline">
                Contact Support →
              </button>
            </Link>
          </GlassCard>

          {/* Report Issue */}
          <GlassCard
            variant="frosted"
            blur="md"
            glow="subtle"
            hover
            className="p-6"
          >
            <Code2 className="h-8 w-8 text-primary mb-3" />
            <h3 className="text-outline-fg-primary font-semibold mb-2">
              Report a Bug
            </h3>
            <p className="text-sm text-foreground/70 mb-4">
              Help improve the site by reporting this issue.
            </p>
            <a
              href={`https://github.com/brianotieno/portfolio/issues/new?title=Error: ${encodeURIComponent(errorMessage)}&body=${encodeURIComponent(`Error ID: ${error.digest || "N/A"}\n\nDescription: `)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:text-primary/80 font-medium hover:underline"
            >
              Open GitHub Issue →
            </a>
          </GlassCard>
        </div>

        {/* Quick Tips */}
        <GlassCard variant="subtle" blur="sm" className="p-6">
          <h4 className="text-outline-fg-primary-soft font-semibold mb-4 text-center">
            Quick Troubleshooting Tips
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-3">
              <div className="text-2xl mb-2">🔄</div>
              <p className="text-xs text-foreground/70">
                Refresh the page and try again
              </p>
            </div>
            <div className="p-3">
              <div className="text-2xl mb-2">🧹</div>
              <p className="text-xs text-foreground/70">
                Clear your browser cache
              </p>
            </div>
            <div className="p-3">
              <div className="text-2xl mb-2">🌐</div>
              <p className="text-xs text-foreground/70">
                Check your internet connection
              </p>
            </div>
          </div>
        </GlassCard>

        {/* Footer */}
        <div className="mt-6 text-center">
          <GlassCard
            variant="frosted"
            blur="sm"
            className="inline-block px-6 py-3"
          >
            <p className="text-xs text-foreground/50 font-mono">
              <span className="text-outline-error-fg-soft">Error Handler</span>{" "}
              | Brian Otieno © {new Date().getFullYear()}
            </p>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}
